import { Alert, FormLabel } from '@cmsgov/ds-medicare-gov';
import Caret from '@cmsgov/ds-medicare-gov/dist/components/Caret/Caret';
import * as PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import local from '../../common/classes/local';
import Password from '../../common/components/account-trouble/enter.password.section';
import LastNameSection from '../../common/components/account-trouble/last.name.section';
import MedicareNumberSection from '../../common/components/account-trouble/medicare.number.section';
import Button from '../../common/components/button';
import DateFields from '../../common/components/date.fields';
import Div from '../../common/components/div';
import H1 from '../../common/components/h1';
import InputField from '../../common/components/input.field';
import StackingButtons from '../../common/components/stacking.buttons';
import StepHeader from '../../common/components/step.header';
import SubHeading from '../../common/components/sub.heading.section';
import TranslatedSpan from '../../common/components/translated.span';
import capitalizeFirstLetter from '../../common/functions/capitalize.first.letter';
import capitalizeWords from '../../common/functions/capitalize.words';
import { getResetPasswordError } from '../../common/functions/get.api.error.message';
import { isValidMedicareNumber } from '../../common/functions/is.valid.medicare.number';
import { isValidYear } from '../../common/functions/is.valid.year';
import translate from '../../common/functions/translate';
import { useConversion, useFormStart, usePageViewWithSteps } from '../../common/hooks/use.analytics';
import useForgotPasswordApi from '../../common/hooks/use.forgot.password.api';
import { usePageTitle } from '../../common/hooks/use.page.title';
import useViewport from '../../common/hooks/use.viewport';
import the from '../../common/objects/the';


const Step = props => {
   const {title, stepNum, stepTitle, combinedWithUsername, allFieldsRequired} = props;
   const step = combinedWithUsername ? stepNum + 1 : stepNum;
   const numSteps = combinedWithUsername ? 4 : 3;
   const pageTitle = combinedWithUsername ? translate('Forgot username and password') : title;
   const showStepNum = step && step <= numSteps;   

   usePageTitle(pageTitle || capitalizeFirstLetter(translate('Reset Password').toLowerCase()));
   usePageViewWithSteps(pageTitle || capitalizeFirstLetter(translate('Reset Password').toLowerCase()), step, stepTitle || title);
   
   return <>
      <Div
         id="step"
         isContainer={true}
         paddingySM={1} paddingyMD={2} paddingyLG={3} paddingyXL={4}
      >
         <Div isRow={true}>
            <Div cols={12}>
               <H1>{pageTitle || capitalizeFirstLetter(translate('Reset Password').toLowerCase())}</H1>
               {combinedWithUsername &&
               <div>
                  <TranslatedSpan english={'Get your username and reset your password.'}/>
               </div>
               }
               {showStepNum &&
               <legend>
                  <Div paddingTop={4} paddingBottom={1}>
                     <StepHeader
                        currentStep={step}
                        numSteps={numSteps}
                        stepTitle={stepTitle}
                     />
                  </Div>
               </legend>
               }
               {allFieldsRequired &&
               <SubHeading style={{fontSize: 14, fontWeight: 400}}>
                  <TranslatedSpan english={'All fields required.'}/>
               </SubHeading>
               }
            </Div>
         </Div>
         {props.children}
      </Div>
   </>
};

const Buttons = props => {
   const {backText, forwardText, onBackClick, onForwardClick, isLoading} = props;
   
   const getBackButton = () => {
      return <>
         <Button
            color="teal"
            displayAs="button"
            fill="outlined"
            onClick={onBackClick}
            size="small"
         >
            {backText}
         </Button>
      </>;
   };
   
   const getForwardButton = () => {
      return <>
         <Button
            color="teal"
            displayAs="button"
            onClick={onForwardClick}
            size="small"
            variation={'primary'}
            isLoading={isLoading}
         >
            {forwardText}
         </Button>
      </>;
   };
   
   return <>
      <StackingButtons
         button1={getBackButton()}
         button2={getForwardButton()}
      />
   </>;
};

const ForgotPassword = props => {
   const startingStep = props.combinedWithUsername ? 1 : 0;
   const question = props.combinedWithUsername ? props.secretQuestion : '';
   const forgotPasswordApi = useForgotPasswordApi();
   const {width} = useViewport();
   
   const [step, setStep] = useState(startingStep);
   
   const [username, setUsername] = useState('');
   const [usernameRef, setUsernameRef] = useState();
   const [medicareNumber, setMedicareNumber] = useState('');
   const [medicareNumRef, setMedicareNumRef] = useState();
   const [lastName, setLastName] = useState('');
   const [lastNameRef, setLastNameRef] = useState();
   const [suffix, setSuffix] = useState('');
   const [dateOfBirth, setDateOfBirth] = useState({day: '', month: '', year: ''});
   const [dayFieldRef, setDayFieldRef] = useState();
   const [monthFieldRef, setMonthFieldRef] = useState();
   const [yearFieldRef, setYearFieldRef] = useState();
   const [forceDateValidation, setForceDateValidation] = useState(false);
   
   const [enterInfoLoading, setEnterInfoLoading] = useState(false);
   const [secretQuestion, setSecretQuestion] = useState(question);
   const [answerQuestionLoading, setAnswerQuestionLoading] = useState(false);
   const [secretAnswer, setSecretAnswer] = useState('');
   const [password, setPassword] = useState('');
   const [passwordRef, setPasswordRef] = useState();
   const [confirmPassword, setConfirmPassword] = useState('');
   const [confirmPasswordRef, setConfirmPasswordRef] = useState();
   const [submitPasswordLoading, setSubmitPasswordLoading] = useState(false);
   const [showPasswords, setShowPasswords] = useState(false);
   const [answerFailCount, setAnswerFailCount] = useState(0);

   const day = translate('day');
   const month = translate('month');
   const year = translate('year');
   
   const formStart = useFormStart('reset_password', step);
   const analyticsComponentName = props.combinedWithUsername ? 'forgot username and password' : 'forgot password';
   const conversion = useConversion(analyticsComponentName, 'reset_password');
   
   const [errors, setErrors] = useState({
      username: false,
      medicareNumber: false,
      lastName: false,
      day: false,
      month: false,
      year: false,
      secretQuestion: false,
      password: false,
      confirmPassword: false
   });
   
   const errorMessages = {
      username: `${translate('Error')}: ${translate('Enter your username.')}`,
      medicareNumber: `${translate('Error')}: ${translate('Enter your Medicare Number.')}`,
      lastName: `${translate('Error')}: ${translate('Enter your last name.')}`,
      day: `${translate('Error')}: ${translate('Enter the {{timePeriod}} you were born.', {timePeriod: day})}`,
      month: `${translate('Error')}: ${translate('Enter the {{timePeriod}} you were born.', {timePeriod: month})}`,
      year: `${translate('Error')}: ${translate('Enter the {{timePeriod}} you were born.', {timePeriod: year})}`,
      secretQuestion: `${translate('Error')}: ${translate('Enter the answer to your secret question.')}`,
      password: `${translate('Error')}: ${translate('Enter your new password.')}`,
      confirmPassword: `${translate('Error')}: ${translate('Re-enter your new password.')}`,
   };
   
   const getErrorMessage = key => errors[key] ? errorMessages[key] : null;
   const [termsAndConditionsDisplayed, setTermsAndConditionsDisplayed] = useState(false);
   const toggleTermsAndConditions = () => setTermsAndConditionsDisplayed(current => !current);
   const [expand, setExpand] = useState(false);
   const [display, setDisplay] = useState(false);
   
   const validateField = (value, errorKey) => {
      const isValid = !!value;
      isValid ? clearError(errorKey) : setError(errorKey, true);
      return isValid;
   };
   
   const validateUsername = (val = username) => validateField(val, 'username');
   const validateMedicareNumber = (val = medicareNumber) => {
      const isValid = isValidMedicareNumber(val);
      isValid ? clearError('medicareNumber') : setError('medicareNumber', true);
      return isValid;
   };
   const validateLastName = (val = lastName) => validateField(val, 'lastName');
   const validateSecretAnswer = (val = secretAnswer) => validateField(val, 'secretQuestion');
   const validatePassword = (val = password) => validateField(val, 'password');
   const validateConfirmPassword = (val = confirmPassword) => {
      const isValid = val === password;
      isValid ? clearError('confirmPassword') : setError('confirmPassword', true);
      return isValid;
   };
   const validateYear = year => {
      const isValid = isValidYear(year);
      isValid ? clearError('year') : setError('year', true);
      return isValid;
   };
   
   const [alertContent, setAlertContent] = useState();
   const [alertTitle, setAlertTitle] = useState();
   
   const lang = local.getItem('language');
   // trigger re-render of alert content when language changes
   useEffect(() => {
      setUpdateAlert(true);
   }, [lang]);
   
   
   const [updateAlert, setUpdateAlert] = useState(false);
   useEffect(() => {
      if (!updateAlert) {
         return;
      }
      setUpdateAlert(false);
   
      const updateAlertContent = () => {
         const actionItems = [];
         const errorStrings = [];
         const addActionItem = (linkText, ref, error) => {
            errorStrings.push(error);
            actionItems.push(
               <li key={linkText} style={{marginTop: '8px'}}>
                  <Button
                     displayAs="link"
                     color="blue"
                     style={{padding: 0}}
                     onClick={() => ref.focus()}
                     analyticsDescription={'focus field'}
                  >
                     {linkText}
                  </Button>
                  : {error}
               </li>
            );
         };
         let isIntermediateFail = true;
         if (errors.username) {
            addActionItem(translate('Username'), usernameRef, translate('Enter your username.'));
         }
         if (errors.medicareNumber) {
            addActionItem(translate('Medicare Number'), medicareNumRef, translate('Enter your Medicare Number.'));
         }
         if (errors.lastName) {
            addActionItem(translate('Last name'), lastNameRef, translate('Enter your last name.'));
         }
         if (errors.month) {
            addActionItem(capitalizeFirstLetter(month), monthFieldRef, translate('Enter the {{timePeriod}} you were born.', {timePeriod: month}));
         }
         if (errors.day) {
            addActionItem(capitalizeFirstLetter(day), dayFieldRef, translate('Enter the {{timePeriod}} you were born.', {timePeriod: day}));
         }
         if (errors.year) {
            addActionItem(capitalizeFirstLetter(year), yearFieldRef, translate('Enter the {{timePeriod}} you were born.', {timePeriod: year}));
         }
         if (errors.password) {
            isIntermediateFail = false;
            addActionItem(translate('Enter new password'), passwordRef, translate('Enter your new password.'));
         }
         if (errors.confirmPassword) {
            isIntermediateFail = false;
            addActionItem(translate('Confirm new password'), confirmPasswordRef, translate('Re-enter your new password.'));
         }
      
         const errorCount = Object.keys(errors).map(key => errors[key] ? 1 : 0).reduce((a, b) => a + b);
         const errorString = errorStrings.join(', ');
      
         if (errorCount >= 1) {
            if (errorString) {
               if (isIntermediateFail) {
                  conversion.failValidationIntermediate(errorString);
               } else  {
                  conversion.failValidationIntermediate(errorString);
                  conversion.failValidation(errorString);
               }
            }
            setAlertTitle(`${translate('Error')}: ${translate('Complete these items to change your password.')}`);
            setAlertContent(<ul style={{paddingLeft: '18px'}}>{actionItems}</ul>);
         } else {
            setAlertTitle(null);
         }
      };
      
      updateAlertContent();
   }, [
      confirmPasswordRef,
      day,
      dayFieldRef,
      errors,
      lastNameRef,
      medicareNumRef,
      month,
      monthFieldRef,
      passwordRef,
      updateAlert,
      usernameRef,
      year,
      yearFieldRef,
      conversion
   ]);
   
   useEffect(() => {
      if (alertContent) {
         const stepEl = document.getElementById('step');
         stepEl.scrollIntoView();
      }
   }, [alertContent]);
   
   useEffect(() => {
      if (props.forgotUsernameData) {
         const {dateOfBirth, lastName, medicareNumber, suffix, username} = props.forgotUsernameData;
         setDateOfBirth(dateOfBirth);
         setLastName(lastName);
         setMedicareNumber(medicareNumber);
         setSuffix(suffix);
         setUsername(username);
      }
   }, [props.forgotUsernameData]);
   
   const setError = (key, val) => {
      setErrors(prevErrors => {
         return {...prevErrors, [key]: val};
      });
   };
   
   const clearError = key => setError(key, false);
   const clearAllErrors = () => {
      for (let key in errors) {
         clearError(key);
      }
   };
   
   const history = useHistory();
   const goToLogin = () => history.push('/login');
   
   const onUsernameChange = event => {
      formStart();
      const newName = event.target.value.trim();
      setUsername(newName);
      validateUsername(newName);
   };
   
   const onMedicareNumberChange = event => {
      formStart();
      const newNum = event.target.value.trim();
      setMedicareNumber(newNum);
      validateMedicareNumber(newNum);
   };
   
   const onLastNameChange = name => {
      formStart();
      name = name.trim();
      setLastName(name);
      validateLastName(name);
   };
   
   const onSuffixChange = suffix => {
      formStart();
      setSuffix(suffix);
   };
   
   const onDateOfBirthChage = dob => {
      formStart();
      setDateOfBirth(dob);
   };
   
   const onSecretAnswerChange = event => {
      formStart();
      const newAns = event.target.value;
      setSecretAnswer(newAns);
      validateSecretAnswer(newAns);
   };
   
   const onPasswordChange = event => {
      formStart();
      const newPass = event.target.value.trim();
      setPassword(newPass);
      validatePassword(newPass);
   };
   
   const onConfirmPasswordChange = event => {
      formStart();
      const newPass = event.target.value.trim();
      setConfirmPassword(newPass);
      validateConfirmPassword(newPass);
   };
   
   const prev = () => {
      setAlertTitle(null);
      setAlertContent(null);
      clearAllErrors();
      setStep(i => i - 1);
   };
   
   const next = () => {
      setAlertTitle(null);
      setAlertContent(null);
      clearAllErrors();
      setStep(i => i + 1);
   };
   
   const getSecretQuestion = async () => {
      return forgotPasswordApi.getSecretQuestion({
         mbi: medicareNumber,
         username,
         lastName,
         suffix,
         dateOfBirth
      });
   };
   
   const submitSecretAnswer = () => {
      return forgotPasswordApi.answerSecretQuestion(medicareNumber, secretQuestion, secretAnswer);
   };
   
   const submitNewPassword = async () => {
      return forgotPasswordApi.resetPassword({
         mbi: medicareNumber,
         username,
         lastName,
         suffix,
         dateOfBirth,
         secretQuestion,
         secretAnswer,
         password
      });
   };
   
   const enterInformationSubmit = async () => {
      if (enterInfoLoading) {
         return;
      }
      
      conversion.attempt();
      
      let hasErrors = false;
      if (!validateUsername()) {
         hasErrors = true;
      }
      if (!validateMedicareNumber()) {
         hasErrors = true;
      }
      if (!validateLastName()) {
         hasErrors = true;
      }
      
      if (!validateField(dateOfBirth.day, 'day')) {
         hasErrors = true;
      }
   
      if (!validateField(dateOfBirth.month, 'month')) {
         hasErrors = true;
      }
      if (!validateYear(dateOfBirth.year)) {
         hasErrors = true;
      }
      
      if (hasErrors) {
         setForceDateValidation(true);
         setUpdateAlert(true);
         return;
      } else {
         setAlertTitle(null);
      }
      
      setForceDateValidation(false);
      setEnterInfoLoading(true);
      try {
         const question = await getSecretQuestion();
         setSecretQuestion(question);
         next();
      } catch (e) {
         let errorMessage;
         if (e.code) {
            const {errors} = e;
            const error = getResetPasswordError(e.code, errors);
            errorMessage = error['applicationErrorMessage'];
            conversion.failAPIIntermediate(error['analyticsErrorMessage']);
         } else {
            // unknown error (not from SLS)
            errorMessage = translate('We can’t process your request at this time because we’re updating our system. We’re sorry for any inconvenience. Please try again later.');
            conversion.failAPIIntermediate(errorMessage);
         }
         setAlertContent(errorMessage);
      } finally {
         setEnterInfoLoading(false);
      }
   };
   
   const secretQuestionSubmit = async () => {
      conversion.attempt();
      if (!validateSecretAnswer()) {
         conversion.failValidationIntermediate('Enter your secret answer');
         return;
      }
      
      try {
         setAnswerQuestionLoading(true);
         await submitSecretAnswer();
         next();
      } catch (e) {
         let errorMessage = '';
         let analyticsMessage = '';
         if (e.code) {
            const {errors} = e;
   
            const isLocked = errors.some(e => e.name === 'BadRequest' && e.message.includes('locked'));
            if (e.code === 403) {
               // wrong answer
               if (answerFailCount === 0) {
                  // first fail
                  errorMessage = `${translate('Error')}: ${translate("This answer doesn't match our records. Re-enter the answer to your secret question.")}`;
                  conversion.failAPIIntermediate(errorMessage);
               } else if (answerFailCount === 1) {
                  // second fail
                  errorMessage = `${translate('Error')}: ${translate("This answer doesn’t match our records. You have 1 more try to re-enter the answer. If the answer doesn't match our records, your account will be locked to protect your information.")}`;
                  conversion.failAPIIntermediate(errorMessage);
               }
               setAnswerFailCount(lastCount => lastCount + 1);
            } else if (e.code === 400 && isLocked) {
               errorMessage = <>
                  {translate('Error')}: {translate('Your account is now locked to protect your information.')}
                  {' '}<Link to="/unlock-account">{translate('Unlock My Account')}.</Link>
               </>;
               analyticsMessage = `${translate('Error')}: ${translate('Your account is now locked to protect your information.')} ${translate('Unlock My Account')}`
               conversion.failAPIIntermediate(analyticsMessage);
            } else {
               errorMessage = getResetPasswordError(e.code, errors)['applicationErrorMessage'];
               conversion.failAPIIntermediate(getResetPasswordError(e.code, errors)['analyticsErrorMessage']);
            }
         } else {
            // unknown error (not from SLS)
               errorMessage = translate('We can’t process your request at this time because we’re updating our system. We’re sorry for any inconvenience. Please try again later.');
               conversion.failAPIIntermediate(errorMessage);
         }
         
         setAlertContent(errorMessage);
      } finally {
         setAnswerQuestionLoading(false);
      }
   };
   
   const resetPassword = async () => {
      if (submitPasswordLoading) {
         return;
      }
      conversion.attempt();
      
      let hasErrors = false;
      if (!validatePassword()) {
         hasErrors = true;
      }
      if (!validateConfirmPassword()) {
         hasErrors = true;
      } else if (password !== confirmPassword) {
         hasErrors = true;
         const error  = `${translate('Error')}: ${translate('The Passwords do not match.')}`
         setAlertContent(error);
         conversion.failValidationIntermediate(error);
         conversion.failValidation(error);
         setConfirmPassword('');
      }
      if (hasErrors) {
         setUpdateAlert(true);
         return;
      } else {
         setAlertTitle(null);
      }
      
      // All should clear password fields
      const correctLength = password.length >= 8 && password.length <= 16;
      const atLeastOneNumber = password.match(/[0-9]/);
      const atLeastOneLetter = password.match(/[a-z]/i);
      const atLeastOneAllowedSpecialChar = password.match(/[@!$%^*()]/);
      
      if (!correctLength || !atLeastOneNumber || !atLeastOneLetter || !atLeastOneAllowedSpecialChar) {
         const error = `${translate('Error')}: ${translate('Complete these items to change your password.')} ${translate('Create a password for your account using the guidelines listed.')}`
         setAlertContent(error);
         conversion.failValidationIntermediate(error);
         conversion.failValidation(error);
         setPassword('');
         setConfirmPassword('');
         return;
      }
      
      const hasDisallowedChars = password.match(/[^0-9a-z@!$%^*()]/i);
      const doesntMeetGuidelinesMessage = `${translate('Error')}: ${translate('The Password does not meet the required guidelines. Please enter the correct information and try again.')}`
      if (hasDisallowedChars) {
         setAlertContent(doesntMeetGuidelinesMessage);
         conversion.failValidationIntermediate(doesntMeetGuidelinesMessage);
         conversion.failValidation(doesntMeetGuidelinesMessage);
         setPassword('');
         setConfirmPassword('');
         return;
      }
      
      setSubmitPasswordLoading(true);
      try {
         await submitNewPassword();
         conversion.success();
         next();
      } catch (e) {
         let errorMessage;
         let analyticsMessage = '';
         if (e.code) {
            const {errors} = e;
            const isLocked = errors.some(e => e.name === 'BadRequest' && e.message.includes('locked'));
            const isAlreadyUsed = errors.some(error => error.name === 'AlreadyUsed');
            const isInvalidPassword = errors.some(e => e.type === 'ValidationError' && e.field === 'newPassword');
            if (isAlreadyUsed) {
               errorMessage = `${translate('Error')}: ${translate("Can't use previously used passwords, please enter a new password.")}`;
               conversion.failAPIIntermediate(errorMessage);
               conversion.failAPI(errorMessage);
            } else if (isLocked) {
               errorMessage = <>
                  {translate('Error')}: {translate('Your account is now locked to protect your information.')}
                  {' '}<Link to="/unlock-account">{translate('Unlock My Account')}.</Link>
               </>;
               analyticsMessage = `${translate('Error')}: ${translate('Your account is now locked to protect your information.')} ${translate('Unlock My Account')}`
               conversion.failAPIIntermediate(analyticsMessage);
               conversion.failAPI(analyticsMessage);
            } else if (isInvalidPassword) {
               errorMessage = doesntMeetGuidelinesMessage;
               conversion.failAPIIntermediate(analyticsMessage);
               conversion.failAPI(analyticsMessage);
            } else {
               errorMessage = getResetPasswordError(e.code, errors)['applicationErrorMessage'];
               conversion.failAPIIntermediate(getResetPasswordError(e.code, errors)['analyticsErrorMessage']);
               conversion.failAPI(getResetPasswordError(e.code, errors)['analyticsErrorMessage']);
            }
         } else {
            // unknown error (not from SLS)
            errorMessage = translate('We can’t process your request at this time because we’re updating our system. We’re sorry for any inconvenience. Please try again later.');
            conversion.failAPIIntermediate(errorMessage);
            conversion.failAPI(errorMessage);
         }
   
         setAlertContent(errorMessage);
      } finally {
         setSubmitPasswordLoading(false);
      }
      
   };
   
   const getAlertContent = () => {
      if (alertContent) {
         return <Alert variation="error">
            {alertTitle}
            {alertContent}
         </Alert>;
      } else {
         return null;
      }
   };
   
   const enterYourInfo = <Step stepNum={1} stepTitle={capitalizeWords(translate('Enter your information'))} allFieldsRequired>
      {getAlertContent()}
      <InputField
         aria-invalid={(!!getErrorMessage('username')).toString()}
         errorMessage={getErrorMessage('username')}
         inputRef={setUsernameRef}
         label={translate('Username')}
         name={'Username'}
         onBlur={() => validateUsername()}
         onChange={onUsernameChange}
         onClick={formStart}
         value={username}
      />
      <MedicareNumberSection
         errorMessage={getErrorMessage('medicareNumber')}
         forceError={!!getErrorMessage('medicareNumber')}
         inputRef={setMedicareNumRef}
         onBlur={() => validateMedicareNumber()}
         onChange={onMedicareNumberChange}
         value={medicareNumber}
      />
      <LastNameSection
         errorMessage={getErrorMessage('lastName')}
         forceError={!!getErrorMessage('lastName')}
         inputRef={setLastNameRef}
         lastNameVal={lastName}
         onLastNameBlur={() => validateLastName()}
         onLastNameChange={onLastNameChange}
         onSuffixChange={onSuffixChange}
         suffixVal={suffix}
      />
      <DateFields
         date={dateOfBirth}
         dayErrorMessage={errorMessages.day}
         dayFieldRef={setDayFieldRef}
         forceValidation={forceDateValidation}
         hint={translate('Use the format MM/DD/YYYY')}
         label={translate('Date of Birth')}
         monthErrorMessage={errorMessages.month}
         monthFieldRef={setMonthFieldRef}
         onBlur={onDateOfBirthChage}
         onChange={onDateOfBirthChage}
         yearErrorMessage={errorMessages.year}
         yearFieldRef={setYearFieldRef}
      />
      <Buttons
         backText={translate('Cancel')}
         forwardText={translate('Next')}
         isLoading={enterInfoLoading}
         onBackClick={goToLogin}
         onForwardClick={enterInformationSubmit}
      />
   </Step>;
   
   const answerSecretQuestion = <Step stepNum={2} stepTitle={translate('Answer secret question')} combinedWithUsername={props.combinedWithUsername} allFieldsRequired>
      {getAlertContent()}
      <FormLabel>
         <TranslatedSpan english={'Secret Question'}/>
      </FormLabel>
   
      <Div>{secretQuestion}</Div>
      <InputField
         aria-invalid={(!!getErrorMessage('secretQuestion')).toString()}
         errorMessage={getErrorMessage('secretQuestion')}
         label={translate('Secret answer')}
         name={'Secret answer'}
         onBlur={() => validateSecretAnswer()}
         onChange={onSecretAnswerChange}
         onClick={formStart}
         value={secretAnswer}
      />
      <Buttons
         backText={translate('Back')}
         forwardText={translate('Next')}
         isLoading={answerQuestionLoading}
         onBackClick={props.onBackClick ? props.onBackClick : prev}
         onForwardClick={secretQuestionSubmit}
      />
   </Step>;
   
   const createNewPassword = <Step stepNum={3} stepTitle={translate('Create new password')} combinedWithUsername={props.combinedWithUsername} allFieldsRequired>
   
      {alertContent &&
      <div style={{marginTop: 8, marginBottom: 16}}>
         {getAlertContent()}
      </div>
      }
      <Div isRow={true} marginBottom={5}>
         <Div cols={width < the.minimumMediumViewportWidth ? 12 : 7}>
            <Password
               columnWidth={width < the.minimumMediumViewportWidth ? 12 : 11}
               errorMessage={getErrorMessage('password')}
               inputRef={setPasswordRef}
               label={translate('Enter new password')}
               onBlur={() => validatePassword()}
               onChange={onPasswordChange}
               onFocus={() => setTermsAndConditionsDisplayed(true)}
               onShowPasswordChange={x => setShowPasswords(x)}
               showPassword={showPasswords}
               value={password}
            />
            {width < the.minimumMediumViewportWidth &&
            <Div>
               <span
                  onClick={toggleTermsAndConditions}
                  style={{
                     color: the.color.tarheelBlue,
                     cursor: 'pointer',
                     textDecoration: 'underline',
                  }}>
                  {`${translate('Password guidelines')} `}
               </span>
               <span
                  onClick={() => {
                     toggleTermsAndConditions();
                     setExpand(!expand);
                     setDisplay(!display);
                  }}>
                  <Caret up={termsAndConditionsDisplayed}></Caret>
               </span>
               <Div style={{display: termsAndConditionsDisplayed ? 'block' : 'none'}}>
                  <div style={{fontSize: '0.9em'}}>
                     <div style={{marginTop: 8}}>
                        <TranslatedSpan english={'Do\'s'}/>
                     </div>
                     <ul className="ds-c-list">
                        <li>
                           <TranslatedSpan english={'Must be 8-16 characters long'}/>
                        </li>
                        <li>
                           <TranslatedSpan english={'Must have at least 1 letter'}/>
                        </li>
                        <li>
                           <TranslatedSpan english={'Must have at least 1 number'}/>
                        </li>
                        <li><TranslatedSpan english={'Must have 1 or more of only these special characters: @ ! $ % ^ * ( )'}/></li>
                        <li>
                           <TranslatedSpan english={'Must be different from your last 6 passwords'}/>
                        </li>
                     </ul>
                     <TranslatedSpan english={'Don\'ts'}/>
                     <ul className="ds-c-list">
                        <li>
                           <TranslatedSpan english={'Can\'t contain your username'}/>
                        </li>
                        <li>
                           <TranslatedSpan english={'Can\'t include your Medicare Number or Social Security number'}/>
                        </li>
                     </ul>
                  </div>
               </Div>
            </Div>}
            <Password
               columnWidth={width < the.minimumMediumViewportWidth ? 12 : 11}
               errorMessage={getErrorMessage('confirmPassword')}
               inputRef={setConfirmPasswordRef}
               label={translate('Confirm new password')}
               onBlur={() => validateConfirmPassword()}
               onChange={onConfirmPasswordChange}
               onShowPasswordChange={x => setShowPasswords(x)}
               showPassword={showPasswords}
               value={confirmPassword}
            />
            <Buttons
               backText={translate('Back')}
               forwardText={translate('Reset Password')}
               isLoading={submitPasswordLoading}
               onBackClick={prev}
               onForwardClick={resetPassword}
            />
         </Div>
         {width > the.minimumMediumViewportWidth &&
         <Div cols={5}>
            <div style={{
               borderLeft: '1px solid #ddd',
               paddingLeft: 16,
               fontSize: '0.9em'
            }}>
               <div className="ds-h5" style={{marginBottom: '16px'}}>
                  <TranslatedSpan english={'Password guidelines'}/>
               </div>
               <div style={{marginTop: '8px'}}>
                  <TranslatedSpan english={'Do\'s'}/>
               </div>
               <ul className="ds-c-list">
                  <li>
                     <TranslatedSpan english={'Must be 8-16 characters long'}/>
                  </li>
                  <li>
                     <TranslatedSpan english={'Must have at least 1 letter'}/>
                  </li>
                  <li>
                     <TranslatedSpan english={'Must have at least 1 number'}/>
                  </li>
                  <li><TranslatedSpan english={'Must have 1 or more of only these special characters: @ ! $ % ^ * ( )'}/></li>
                  <li>
                     <TranslatedSpan english={'Must be different from your last 6 passwords'}/>
                  </li>
               </ul>
               <TranslatedSpan english={'Don\'ts'}/>
               <ul className="ds-c-list">
                  <li>
                     <TranslatedSpan english={'Can\'t contain your username'}/>
                  </li>
                  <li>
                     <TranslatedSpan english={'Can\'t include your Medicare Number or Social Security number'}/>
                  </li>
               </ul>
            </div>
         </Div>}
      </Div>
   </Step>;
   
   const success = <Step stepNum={4} title={translate('Your password is reset')} combinedWithUsername={props.combinedWithUsername}>
      <Alert variation="success" hideIcon={true}>
         <TranslatedSpan english={'You\'re all set! Your password is now changed. You can now log in.'}/>
      </Alert>
      <Div
         isRow={true}
         paddingBottom={3}
         paddingTop={2}
         style={{paddingLeft: 16}}>
         <Button displayAs="button" color="teal" size="small" variation="primary" onClick={goToLogin}>
            <TranslatedSpan english={'Return to Log in'}/>
         </Button>
      </Div>
   </Step>;
   
   
   const steps = [enterYourInfo, answerSecretQuestion, createNewPassword, success];
   
   return <>
      <fieldset style={{
         border: 0,
         margin: 0,
         padding: 0,
      }}>
         {steps[step]}
      </fieldset>
   </>;
};

ForgotPassword.propTypes = {
   combinedWithUsername: PropTypes.bool,
   secretQuestion: PropTypes.string,
   onBackClick: PropTypes.func,
   forgotUsernameData: PropTypes.object
};

export default ForgotPassword;